<div class="roster-container">
  @if (exportQuery && !accessGated && editable) {
    <div class="roster-toolbar">
      @if (importQuery) {
        <button mat-button (click)="openImportDialog()" class="roster-toolbar-button">
          <mat-icon class="roster-toolbar-icon">upload_file</mat-icon>
          Import
        </button>
      }
      <button mat-button [matMenuTriggerFor]="exportMenu" class="roster-toolbar-button">
      <mat-icon class="roster-toolbar-icon">download</mat-icon>
        Export
      </button>
      <mat-menu #exportMenu="matMenu">
        <button mat-menu-item (click)="triggerExport(false)">Export Full</button>
        <button mat-menu-item (click)="triggerExport(true)">Export Condensed</button>
      </mat-menu>
    </div>
  }

  @if (rosterTitle === 'Not Yet Certified' && !showAdminActions) {
    <div class="roster-info-banner">
      <p>
        The Certification Roster displays students who chose to attend your school when they accepted the Opportunity
        Scholarship and/or ESA+. By completing the certification process, you are certifying to a state government
        agency that each student you certify is enrolled in your school for this academic year. See
        <a
          href="https://k12.ncseaa.edu/school-admins/managing-funds/certification-and-endorsement/"
          target="_blank"
          rel="noopener noreferrer"
          >Certification Instructions</a
        >
        for more information.
      </p>
    </div>
  } @else if (rosterTitle === 'Not Yet Endorsed' && !showAdminActions) {
    <div class="roster-info-banner">
      <p>By endorsing a student, I attest that:</p>
      <ul>
        <li>
          I have reviewed the cost information for these students, and it is true and correct to the best of my
          knowledge.
        </li>
        <li>
          I understand that if I have certified a student incorrectly, and the student is determined to be ineligible
          for the Opportunity Scholarship and/or ESA+ Program, the school may be required to return the disbursed funds
          to the SEAA.
        </li>
        <li>
          I represent and warrant that I am employed by {{ currentSchool?.name }} and I am authorized to complete this
          endorsement on behalf of the school.
        </li>
      </ul>
    </div>
  }

  @if (!accessGated && students.length > 0) {
    <cfi-mat-table
      [data]="students"
      [columnDefinitions]="columnDefinitions"
      [displayedColumns]="displayedColumns"
      [columnsToDisplayWithExpand]="displayedColumns"
      [(expandedElement)]="expandedStudent"
      [paginated]="true"
      [serverSidePaging]="true"
      [sortKey]="currentSortColumn"
      [sortDirection]="currentSortDirection"
      [totalRows]="totalCount"
      [currentPageIndex]="currentPageIndex"
      [currentPageSize]="currentPageSize"
      [pageSizeOptions]="[10, 25, 50, 100]"
      [defaultPageSize]="250"
      [allowExpand]="true"
      (cbPaginate)="onPageChange($event)"
      (cbSort)="onSort($event)">
      <!-- Header Template -->
      <ng-template #headerTemplate let-column>
        {{ column.header }}
      </ng-template>

      <!-- Cell Template -->
      <ng-template #cellTemplate let-student let-column="column">
        @switch (column.name) {
          @case ('studentDisplayId') {
            @if (studentRecordBasePath && student.studentId) {
              <a
                class="student-record-link"
                [routerLink]="[studentRecordBasePath, student.studentId]"
                [queryParams]="studentRecordQueryParams"
                (click)="$event.stopPropagation()"
                [attr.data-pw]="'roster_student_record_link'"
                [attr.data-qa]="'roster_student_record_link'">
                {{ student.studentDisplayId || '—' }}
              </a>
            } @else {
              <div>{{ student.studentDisplayId || '—' }}</div>
            }
          }
          @case ('studentFirstName') {
            <div>{{ student.studentFirstName }}</div>
          }
          @case ('studentLastName') {
            <div>{{ student.studentLastName }}</div>
          }
          @case ('parentName') {
            <div>{{ getParentDisplayName(student) }}</div>
          }
          @case ('programAccepted') {
            <div>
              {{ getFormattedAwards(student) }}
            </div>
          }
          @case ('awardTier') {
            <div>{{ student.awardTier || '—' }}</div>
          }
          @case ('esaAwardLevel') {
            <div>{{ student.esaAwardLevel || '—' }}</div>
          }
          @case ('transferStatusId') {
            <div>
              @if (student.transferStatusId === 0) {
                Pending
              } @else if (student.transferStatusId === 1) {
                Complete
              } @else {
                —
              }
            </div>
          }
          @case ('withdrawalReasonName') {
            <div>{{ student.withdrawalReasonName || '—' }}</div>
          }
          @case ('weeksOfAttendance') {
            <div>{{ student.weeksOfAttendance ?? '—' }}</div>
          }
          @case ('endDate') {
            <div>{{ (student.endDate | date: 'MM/dd/yyyy') || '—' }}</div>
          }
          @case ('isParentEndorsed') {
            <div class="icon-container">
              @if (student.isParentEndorsed === true) {
                <svg
                  class="status-icon endorsed"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px">
                  <path
                    d="M702-480 560-622l57-56 85 85 170-170 56 57-226 226Zm-455-47q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm80-80h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm296.5-343.5Q440-607 440-640t-23.5-56.5Q393-720 360-720t-56.5 23.5Q280-673 280-640t23.5 56.5Q327-560 360-560t56.5-23.5ZM360-300Zm0-340Z" />
                </svg>
              } @else if (student.isParentEndorsed === false) {
                <svg
                  class="status-icon not-endorsed"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px">
                  <path
                    d="m696-440-56-56 83-84-83-83 56-57 84 84 83-84 57 57-84 83 84 84-57 56-83-83-84 83Zm-449-87q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm80-80h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm296.5-343.5Q440-607 440-640t-23.5-56.5Q393-720 360-720t-56.5 23.5Q280-673 280-640t23.5 56.5Q327-560 360-560t56.5-23.5ZM360-640Zm0 400Z" />
                </svg>
              } @else {
                <svg
                  class="status-icon pending"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px">
                  <path
                    d="M791-55 686-160H160v-112q0-34 17.5-62.5T224-378q45-23 91.5-37t94.5-21L55-791l57-57 736 736-57 57ZM240-240h366L486-360h-6q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32Zm496-138q29 14 46 42.5t18 61.5L666-408q18 7 35.5 14t34.5 16ZM568-506l-59-59q23-9 37-29.5t14-45.5q0-33-23.5-56.5T480-720q-25 0-45.5 14T405-669l-59-59q23-34 58-53t76-19q66 0 113 47t47 113q0 41-19 76t-53 58Zm38 266H240h366ZM457-617Z" />
                </svg>
              }
            </div>
          }
        }
      </ng-template>

      <!-- Expanded Row Template -->
      <ng-template #expandedRow let-studentData>
        <div class="roster-expanded-row">
          @if (studentCardComponent) {
            <ng-container
              *ngComponentOutlet="
                studentCardComponent;
                inputs: {
                  student: studentData,
                  editable: editable,
                  showAdminActions: showAdminActions,
                  disableBackwardMove: disableBackwardMove,
                  schoolTermGrades: schoolTermGrades,
                  schoolGradeYears: schoolGradeYears,
                  attendanceTypes: attendanceTypes,
                  issueRestrictions: issueRestrictions,
                  isExpanded: true,
                  onActionCompleted: onStudentActionCompleted.bind(this),
                  onCertifiedCallback: onStudentCertified.bind(this),
                  onEndorsedCallback: onStudentEndorsed.bind(this),
                  onSavedCallback: onStudentSaved.bind(this),
                  onRevertedCallback: onStudentReverted.bind(this),
                  onWithdrawalCallback: onWithdrawal.bind(this)
                }
              ">
            </ng-container>
          }
        </div>
      </ng-template>
    </cfi-mat-table>
  }
  @else if (!isLoading && emptyStateMessage) {
    <p class="empty-state-message">{{ emptyStateMessage }}</p>
  }
</div>
